'use client'

/**
 * SubmissionRecoveryProvider (Issue #3781, #3787)
 *
 * Global mount point for useSubmissionRecovery.
 * Retries pending cached submissions on mount, online, and
 * cadence:server-reachable events.
 *
 * Routes cached submissions to the appropriate server action
 * based on the cache key prefix (submissionType:contextId).
 *
 * Issue #3787 - Added reflection, survey-response, and comment routing
 */

import { useSubmissionRecovery } from '@/lib/submission-recovery'
import { createUpdate } from '@/app/actions/create-update'
import { submitReflection } from '@/app/actions/reflections'
import { submitSurveyResponse } from '@/app/actions/surveys'
import { createComment } from '@/app/actions/comments'
import { reportClientError } from '@/lib/client-error'
import { ERROR_IDS } from '@/lib/errors/constants'
import type { EntityType } from '@prisma/client-cadence'

// Type-safe recovery handlers for each submission type
type RecoveryHandler = (data: unknown, submissionId: string) => Promise<boolean>

const recoveryHandlers: Record<string, RecoveryHandler> = {
  update: async (data, submissionId) => {
    const updateData = data as {
      itemId: string
      itemType: 'rock' | 'group' | 'project'
      input: Parameters<typeof createUpdate>[2]
    }
    // Issue #3789 - Pass submissionId for server-side idempotency
    const result = await createUpdate(updateData.itemId, updateData.itemType, {
      ...updateData.input,
      submissionId,
    })
    return result.success === true
  },

  reflection: async (data, submissionId) => {
    const reflectionData = data as { reflectionId: string }
    // Issue #7809 - forward submissionId so recovery-driven retries dedupe
    // server-side (parallels the `update` handler above per Issue #3789).
    const result = await submitReflection(
      reflectionData.reflectionId,
      submissionId,
    )
    return result.success === true
  },

  'survey-response': async data => {
    const surveyData = data as {
      surveyId: string
      answers: Parameters<typeof submitSurveyResponse>[1]
    }
    const result = await submitSurveyResponse(
      surveyData.surveyId,
      surveyData.answers,
    )
    return result.success === true
  },

  comment: async data => {
    const commentData = data as {
      content: string
      entityType: EntityType
      entityId: string
    }
    const result = await createComment({
      content: commentData.content,
      entityType: commentData.entityType,
      entityId: commentData.entityId,
    })
    return result.success === true
  },
}

export function SubmissionRecoveryProvider() {
  useSubmissionRecovery(async (key, data, submissionId) => {
    if (!data) return false

    // Route based on cache key prefix: submissionType:contextId
    const [submissionType] = key.split(':')
    const handler = recoveryHandlers[submissionType]

    if (!handler) {
      console.info(
        `[SubmissionRecovery] Unknown submission type: "${submissionType}" (key: ${key})`,
      )
      return false
    }

    try {
      return await handler(data, submissionId)
    } catch (error) {
      reportClientError({
        errorId: ERROR_IDS.SUBMISSION_RECOVERY_PROVIDER_RESTORE_FAILED,
        action: 'restoreSubmission',
        error,
        context: { submissionType, key },
      })
      return false
    }
  })

  return null
}
